import React from "react";
import { Button, Col, message, Row, Tabs } from "antd";
import { withRouter } from "next/router";
import styles from "@aspen/theme/History.module.less";
import { IExportHistoryCategory, type IInjectProps } from "@aspen/model";
import {
  getQueryValue,
  HISTORY_PATHS,
  i18nUtil,
  isClient,
  PRODUCT_TYPES,
  USER_AUTH,
  USER_ROLE_POWER,
} from "@aspen/libs";
import {
  TradeHistoryTab,
  YieldHistoryTab,
  StructuredHistoryTab,
  FundsHistoryTab,
  DealsHistoryTab,
  ModalExportHistory,
  ExportStatus,
  type DateRange,
} from "@aspen/widgets";
import { exportHistory, exportHistoryPreview } from "@aspen/services";

interface IState {
  currentTab: string;
  exportModalVisible: boolean;
}
class History extends React.PureComponent<IInjectProps, IState> {
  constructor(props: IInjectProps) {
    super(props);
    this.state = {
      currentTab: PRODUCT_TYPES.spot,
      exportModalVisible: false,
    };
  }

  componentDidMount() {
    const defaultTab =
      this.props?.router?.query?.id?.toString() ||
      (isClient && getQueryValue("id")) ||
      PRODUCT_TYPES.spot;
    this.setState({ currentTab: defaultTab });
  }

  render(): React.ReactNode {
    const intl = i18nUtil.t();
    const { currentTab, exportModalVisible } = this.state;

    const tabItems = [
      {
        label: intl["title.market"],
        key: PRODUCT_TYPES.spot,
      },
      {
        label: intl["compliance.yield.title"],
        key: PRODUCT_TYPES.yield,
      },
      {
        label: intl["history.structured"],
        key: PRODUCT_TYPES.structured,
      },
      {
        label: intl["menu.strategies"],
        key: PRODUCT_TYPES.strategies,
      },
      {
        label: intl["menu.ventures"],
        key: PRODUCT_TYPES.ventures,
      },
    ];

    const historyTabMap = {
      [PRODUCT_TYPES.spot]: TradeHistoryTab,
      [PRODUCT_TYPES.yield]: YieldHistoryTab,
      [PRODUCT_TYPES.structured]: StructuredHistoryTab,
      [PRODUCT_TYPES.strategies]: FundsHistoryTab,
      [PRODUCT_TYPES.ventures]: DealsHistoryTab,
    };
    const CurrentTabComponent = historyTabMap[currentTab];

    const user_auth = (isClient && localStorage.getItem(USER_AUTH)) || "";

    return (
      <>
        <section className={`${styles.history} ` + `customer`}>
          <div className="customerTop">
            <Row className="top">
              <Col span={24}>
                <div className={styles.titleContent}>
                  <p className="title">{intl["history.title"]}</p>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={() => {
                        this.setState({ exportModalVisible: true });
                      }}
                      className={styles.exportBtn}>
                      {intl["export.history.title"]}
                    </Button>
                    {USER_ROLE_POWER[user_auth]?.withdrawApplication ? (
                      <Button
                        type="primary"
                        className={styles.btn}
                        onClick={() => {
                          this.props.router.push(HISTORY_PATHS.HISTORY_APPLICATION_DETAIL);
                        }}>
                        {intl["wallet.withdraw.application.title"]}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
          <div className={styles.tableWrap}>
            <div className="section" style={{ width: "100%", marginTop: 0 }}>
              <div className={styles.history} style={{ marginTop: 0 }}>
                <Tabs
                  items={tabItems}
                  className={styles.tab}
                  activeKey={currentTab}
                  onChange={(value: string) => {
                    this.setState({ currentTab: value });
                  }}
                />
                <div className={styles.historyArea}>
                  <CurrentTabComponent />
                </div>
              </div>
            </div>
          </div>
        </section>
        {exportModalVisible ? (
          <ModalExportHistory
            descriptions={[
              { text: intl["export.history.description"][0] },
              { text: intl["export.history.description"][1], className: "text-[#f17e00]" },
              { text: intl["export.history.description"][2] },
            ]}
            open={exportModalVisible}
            onClose={() => {
              this.setState({ exportModalVisible: false });
            }}
            onExport={async (dateRange: DateRange): Promise<ExportStatus> => {
              if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
                return ExportStatus.INIT;
              }
              try {
                const startTime = new Date(dateRange.startDate).getTime();
                const endTime = new Date(dateRange.endDate).getTime();
                const exportParams = {
                  category: IExportHistoryCategory.MY_INVESTMENT,
                  startTime,
                  endTime,
                };
                // 1. 调用当前选中的时间范围内是否有历史记录
                const previewResult = await exportHistoryPreview(exportParams);
                // 2. 如果没有，则直接返回 ExportStatus.NO_RECORDS
                if (!previewResult?.data) {
                  return ExportStatus.NO_RECORDS;
                }
                // 3. 如果有，则调用导出接口，接口调用成功后，返回 ExportStatus.SUCCESS
                const exportResult = await exportHistory(exportParams);
                if (exportResult) {
                  return ExportStatus.EXPORT_SUCCESS;
                } else {
                  message.error(intl["system.error"]);
                  return ExportStatus.INIT;
                }
              } catch (error) {
                message.error(intl["system.error"]);
                return ExportStatus.INIT;
              }
            }}
          />
        ) : null}
      </>
    );
  }
}

export const PageHistory = withRouter(History);
